import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, ArrowLeft, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAdminGameState } from '../hooks';
import { GameStateEditor } from '../components';

export function AdminUserStateDetailPage() {
  const { username, keyword } = useParams<{ username: string; keyword: string }>();
  const navigate = useNavigate();
  const { gameState, loading, error, refresh } = useAdminGameState(username || '', keyword || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const backPath = `/a/arche_dfe_game_state/admin/users/${encodeURIComponent(username || '')}`;

  const handleSave = async (data: string, _version: number) => {
    if (!username || !keyword || !gameState) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.adminSetGameState(username, keyword, {
        dataType: gameState.dataType,
        data,
      });
      refresh();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save game state');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!username || !keyword) return;
    setDeleting(true);
    try {
      await api.adminDeleteGameState(username, keyword);
      navigate(backPath);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to delete game state');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !gameState) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              {error ? 'Error' : 'Not Found'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {error || `Game state not found for keyword: ${keyword}`}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        {confirmDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Are you sure?</span>
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Confirm Delete'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">User: {username}</p>
      <GameStateEditor state={gameState} onSave={handleSave} saving={saving} error={saveError} />
    </div>
  );
}
